// Modal showing Gumroad UE plugin transfer policy (legal/transfer-policies/... via API)

import { useEffect, useRef, useState } from 'react';
import { RevoltModal } from '../../../components';
import {
  parseMarkdown,
  setupCodeBlockCopyButtons,
  setupExternalLinkHandlers,
  renderMath,
  useManager,
} from '../../../core';
import { AccountManager } from '../managers/account-manager';
import './TransferPolicyModal.css';

export function TransferPolicyModal({ isOpen, onClose }) {
  const { manager: accountManager } = useManager(AccountManager);
  const bodyRef = useRef(null);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !accountManager) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const md = await accountManager.fetchTransferPolicyMarkdown();
        if (!cancelled) setMarkdown(md);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load transfer policy');
          setMarkdown('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, accountManager]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!isOpen || !el || error || loading) return;

    const md = markdown?.trim();
    if (!md) {
      el.innerHTML = '';
      return;
    }

    el.innerHTML = parseMarkdown(md);
    setupCodeBlockCopyButtons(el);
    setupExternalLinkHandlers(el);
    renderMath(el);
  }, [isOpen, markdown, loading, error]);

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title="Gumroad: Revolt Unreal Engine Plugin Transfer Policy"
      containerClassName="modal-800 transfer-policy-modal-container"
    >
      <div className="transfer-policy-modal-inner">
        {loading && (
          <div className="transfer-policy-modal-status">Loading policy…</div>
        )}
        {error && (
          <div className="transfer-policy-modal-status transfer-policy-modal-status--error">
            {error}
          </div>
        )}
        {!loading && !error && (
          <div ref={bodyRef} className="transfer-policy-modal-markdown" />
        )}
      </div>
    </RevoltModal>
  );
}
